import { requireActiveOrg } from '../lib/config'
import { FlightDeskAPI } from '../lib/api'

interface TaskCreateOptions {
  project: string
  title: string
  description?: string
  subproject?: string
}

interface TaskListOptions {
  project?: string
  status?: string
}

interface TaskStatusOptions {
  taskId: string
}

interface TaskUpdateOptions {
  taskId: string
  status?: string
  branch?: string
  prUrl?: string
  prNumber?: string
  session?: string
}

interface TaskSyncOptions {
  taskId: string
}

type TaskOptions = TaskCreateOptions | TaskListOptions | TaskStatusOptions | TaskUpdateOptions | TaskSyncOptions

export async function taskCommand(
  action: 'create' | 'list' | 'status' | 'update' | 'sync',
  options: TaskOptions
): Promise<void> {
  const { config, org } = requireActiveOrg()
  const api = FlightDeskAPI.fromConfig(config, org)

  try {
    switch (action) {
      case 'create':
        await handleCreate(api, options as TaskCreateOptions)
        break
      case 'list':
        await handleList(api, options as TaskListOptions)
        break
      case 'status':
        await handleStatus(api, options as TaskStatusOptions)
        break
      case 'update':
        await handleUpdate(api, options as TaskUpdateOptions)
        break
      case 'sync':
        await handleSync(api, options as TaskSyncOptions)
        break
    }
  } catch (error) {
    console.error(`Error: ${error}`)
    process.exit(1)
  }
}

async function handleCreate(api: FlightDeskAPI, options: TaskCreateOptions): Promise<void> {
  console.log(`Creating task: ${options.title}`)

  const task = await api.createTask({
    projectId: options.project,
    title: options.title,
    description: options.description,
    subprojectId: options.subproject,
  })

  console.log(`\n✅ Task created`)
  console.log(`   ID: ${task.id}`)
  console.log(`   Title: ${task.title}`)
  console.log(`   Status: ${task.status}`)
}

async function handleList(api: FlightDeskAPI, options: TaskListOptions): Promise<void> {
  const tasks = await api.listTasks({
    projectId: options.project,
    status: options.status,
  })

  if (tasks.length === 0) {
    console.log('No tasks found.')
    return
  }

  console.log('\nTasks:\n')
  for (const task of tasks) {
    const statusEmoji = getStatusEmoji(task.status)
    console.log(`${statusEmoji} ${task.id.slice(0, 8)} - ${task.title}`)
    if (task.project?.name) console.log(`   Project: ${task.project.name}`)
    console.log(`   Status: ${task.status}`)
    if (task.branchName) console.log(`   Branch: ${task.branchName}`)
    if (task.prUrl) console.log(`   PR: ${task.prUrl}`)
    console.log('')
  }
}

async function handleStatus(api: FlightDeskAPI, options: TaskStatusOptions): Promise<void> {
  const task = await api.getTask(options.taskId) as {
    id: string
    title: string
    description?: string
    status: string
    branchName?: string
    prUrl?: string
    prNumber?: number
    sessionViewUrl?: string
    sessionTeleportId?: string
    project: { name: string; githubRepo: string }
    createdAt: string
    updatedAt: string
  }

  if (!task) {
    console.error('Task not found')
    process.exit(1)
  }

  console.log(`\n📋 Task: ${task.title}\n`)
  console.log(`ID:       ${task.id}`)
  console.log(`Status:   ${getStatusEmoji(task.status)} ${task.status}`)
  if (task.project) {
    console.log(`Project:  ${task.project.name}${task.project.githubRepo ? ` (${task.project.githubRepo})` : ''}`)
  }

  if (task.description) {
    console.log(`\nDescription:\n${task.description}`)
  }

  console.log('')
  if (task.branchName) console.log(`Branch:   ${task.branchName}`)
  if (task.prUrl) console.log(`PR:       ${task.prUrl}`)
  if (task.prNumber) console.log(`PR #:     ${task.prNumber}`)
  if (task.sessionViewUrl) console.log(`Session:  ${task.sessionViewUrl}`)
  if (task.sessionTeleportId) {
    console.log(`Resume:   claude --teleport ${task.sessionTeleportId}`)
  }

  console.log(`\nCreated:  ${new Date(task.createdAt).toLocaleString()}`)
  console.log(`Updated:  ${new Date(task.updatedAt).toLocaleString()}`)
}

/**
 * Extract session ID from a URL, teleport command, or raw session ID.
 * Accepts:
 *   - https://claude.ai/code/session_01JRaERFezbgYUGeHdtSZKmZ
 *   - session_01JRaERFezbgYUGeHdtSZKmZ
 */
function parseSessionId(value: string): string {
  const urlPrefix = 'https://claude.ai/code/'
  if (value.startsWith(urlPrefix)) {
    return value.slice(urlPrefix.length)
  }
  return value
}

async function handleUpdate(api: FlightDeskAPI, options: TaskUpdateOptions): Promise<void> {
  const input: Record<string, unknown> = {}

  if (options.status) input.status = options.status
  if (options.branch) input.branchName = options.branch
  if (options.prUrl) {
    input.prUrl = options.prUrl
    const prNumberMatch = options.prUrl.match(/\/pull\/(\d+)/)
    if (prNumberMatch) {
      input.prNumber = parseInt(prNumberMatch[1], 10)
    }
  }
  if (options.prNumber) {
    input.prNumber = parseInt(options.prNumber, 10)
  }

  if (options.session) {
    const sessionId = parseSessionId(options.session)
    input.sessionViewUrl = `https://claude.ai/code/${sessionId}`
    input.sessionTeleportId = sessionId
  }

  if (Object.keys(input).length === 0) {
    console.error('No updates specified. Use --status, --branch, --pr-url, or --session')
    process.exit(1)
  }

  // Auto-transition: setting a branch on a DISPATCHED task moves it to IN_PROGRESS
  if (options.branch && !options.status) {
    const current = await api.getTask(options.taskId) as { status: string }
    if (current?.status === 'DISPATCHED') {
      input.status = 'IN_PROGRESS'
      console.log(`   Auto-transitioning DISPATCHED → IN_PROGRESS`)
    }
  }

  console.log(`Updating task ${options.taskId}...`)

  const task = await api.updateTask(options.taskId, input)

  console.log(`\n✅ Task updated`)
  console.log(`   Status: ${task.status}`)
  if (task.branchName) console.log(`   Branch: ${task.branchName}`)
  if (task.prUrl) console.log(`   PR: ${task.prUrl}`)
  if (task.prNumber) console.log(`   PR #: ${task.prNumber}`)
  if (task.sessionViewUrl) console.log(`   Session: ${task.sessionViewUrl}`)

  // Auto-sync PR review state when a PR URL is set
  if (options.prUrl) {
    console.log(`\n🔄 Syncing PR review state...`)
    try {
      const syncResult = await api.syncTaskChecks(options.taskId)
      printSyncResult(syncResult)
    } catch (err) {
      console.log(`   ⚠️  Sync failed (checks will update when webhooks arrive): ${err}`)
    }
  }
}

async function handleSync(api: FlightDeskAPI, options: TaskSyncOptions): Promise<void> {
  console.log(`Syncing PR review state for task ${options.taskId}...`)

  const syncResult = await api.syncTaskChecks(options.taskId)
  printSyncResult(syncResult)
}

function printSyncResult(result: { synced: number; checks: Array<{ integration: string; reviewer: string; status: string }> }): void {
  if (result.synced === 0) {
    console.log(`   No review feedback found on the PR.`)
    return
  }

  console.log(`\n   Found ${result.synced} review check(s):`)
  for (const check of result.checks) {
    const icon = getCheckIcon(check.status)
    console.log(`   ${icon} ${check.integration} (${check.reviewer}): ${check.status}`)
  }
}

function getCheckIcon(status: string): string {
  switch (status) {
    case 'PASSED': return '✅'
    case 'FAILED': return '❌'
    case 'PENDING': return '⏳'
    case 'SKIPPED': return '⏭️'
    default: return '❓'
  }
}

function getStatusEmoji(status: string): string {
  switch (status) {
    case 'PENDING': return '⬜'
    case 'DISPATCHED': return '🚀'
    case 'IN_PROGRESS': return '🔄'
    case 'BRANCH_CREATED': return '🌿'
    case 'PR_OPEN': return '📝'
    case 'PREVIEW_STARTING': return '⏳'
    case 'PREVIEW_READY': return '🌐'
    case 'REVIEW_RUNNING': return '🔍'
    case 'REVIEW_DONE': return '✅'
    case 'QA_READY': return '🧪'
    case 'QA_APPROVED': return '👍'
    case 'MERGED': return '🎉'
    case 'ARCHIVED': return '📦'
    default: return '❓'
  }
}
